import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const slugify = (str) =>
  (str || '')
    .toLowerCase()
    .replace(/[^a-z0-9а-я]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);

const emptyForm = { key: '', name_ru: '', name_en: '', order: 0, is_active: true };

const DocCategoriesManager = ({ categories, onChanged, notify }) => {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const sorted = [...categories].sort((a, b) => a.order - b.order);

  const openModal = (cat = null) => {
    if (cat) {
      setEditing(cat);
      setForm({ key: cat.key, name_ru: cat.name_ru || '', name_en: cat.name_en || '', order: cat.order || 0, is_active: cat.is_active });
    } else {
      setEditing(null);
      setForm({ ...emptyForm, order: categories.length });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'name_ru' && !editing && !prev.key) next.key = slugify(value);
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing) {
        await axios.put(`${API_URL}/api/admin/document-categories/${editing.id}`, payload, { headers: getAuthHeaders() });
        notify('Категория обновлена');
      } else {
        await axios.post(`${API_URL}/api/admin/document-categories`, payload, { headers: getAuthHeaders() });
        notify('Категория создана');
      }
      closeModal();
      onChanged();
    } catch (error) {
      notify(error.response?.data?.detail || 'Ошибка сохранения категории', 'error');
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm('Удалить категорию документов?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/document-categories/${cat.id}`, { headers: getAuthHeaders() });
      notify('Категория удалена');
      onChanged();
    } catch (error) {
      notify(error.response?.data?.detail || 'Ошибка удаления', 'error');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-doc-categories">
      <div className="flex justify-end">
        <button
          onClick={() => openModal()}
          data-testid="admin-doc-add-category-btn"
          className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-lg font-medium transition"
        >
          + Добавить категорию
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm divide-y divide-gray-100">
        {sorted.map((cat) => (
          <div key={cat.id} className="flex items-center justify-between px-5 py-3">
            <div className="flex items-center gap-3">
              <span className="text-xs font-mono bg-gray-100 text-gray-500 px-2 py-0.5 rounded">{cat.order}</span>
              <span className="font-semibold text-gray-900">{cat.name_ru}</span>
              <span className="text-sm text-gray-400">/ {cat.name_en}</span>
              <span className="text-xs font-mono text-gray-400">({cat.key})</span>
              {!cat.is_active && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">скрыта</span>}
            </div>
            <div className="flex items-center gap-3 text-sm">
              <button onClick={() => openModal(cat)} className="text-blue-600 hover:text-blue-800">Изменить</button>
              <button onClick={() => handleDelete(cat)} className="text-red-600 hover:text-red-800">Удалить</button>
            </div>
          </div>
        ))}
        {sorted.length === 0 && (
          <div className="text-center py-8 text-gray-500">Нет категорий. Добавьте первую.</div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-5 border-b">
              <h3 className="text-xl font-bold text-gray-900">{editing ? 'Редактировать категорию' : 'Новая категория'}</h3>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Название (RU) *</label>
                  <input type="text" name="name_ru" value={form.name_ru} onChange={handleChange} required data-testid="admin-doc-cat-name-ru" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Название (EN) *</label>
                  <input type="text" name="name_en" value={form.name_en} onChange={handleChange} required className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ключ (key) *</label>
                  <input type="text" name="key" value={form.key} onChange={handleChange} required disabled={!!editing} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 font-mono text-sm disabled:bg-gray-100" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Порядок</label>
                  <input type="number" name="order" value={form.order} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                </div>
              </div>
              <div className="flex items-center">
                <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange} className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded" />
                <label className="ml-2 block text-sm text-gray-900">Показывать на сайте</label>
              </div>
              <div className="flex justify-end space-x-3 pt-3 border-t">
                <button type="button" onClick={closeModal} className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Отмена</button>
                <button type="submit" data-testid="admin-doc-cat-save-btn" className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg">{editing ? 'Сохранить' : 'Создать'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocCategoriesManager;
